import { Wizard, createFireboltProvider } from '@iq-firebolt/client'
import classNames from "classnames"

import { partnerConfig, withAnalyticsProvider } from '../../configs'
import useRedirectTransitionError from 'hooks/useRedirectTransitionError'

import StepFormLayout from 'components/Layouts/StepFormLayout'
import DefaultFormStep from 'components/StepTemplates/DefaultFormStep'
import LoadingStep from 'components/StepTemplates/LoadingStep'
import SMSTokenStep from 'components/StepTemplates/SMSTokenStep'

import BankDataStep from '../../steps-templates/BankDataStep'
import ComplementaryData from '../../steps-templates/ComplementaryData'
import AuthorizeSMSStep from '../../steps-templates/AuthorizeSMSStep'
import CustomSelectStep from '../../steps-templates/CustomSelectStep'
import FirstStep from '../../steps-templates/FirstStep'
import IdentityProof from '../../steps-templates/IdentityProof'
import OccupationStep from '../../steps-templates/OccupationStep'
import UploadStep from '../../steps-templates/UploadStep'
import IncomeData from '../../steps-templates/IncomeData'
import IdentityDocument from '../../steps-templates/IdentityDocument'

import { phoneNumberStepConfig, addressStepConfig } from './customStepsConfig'
import useBbOurocardFormFlow from './hook'

import { useDowngradeModal } from 'hooks/useDowngradeModal'

import './style.styl'
import '../../styles/theme-ac.styl'
import '../../styles/theme-cp.styl'
import { FormAlerts } from '../../components/FormAlerts'
import { useUploadContext } from '../../hooks/useUploadContext'
import { UploadContext } from '../../contexts/uploadContext'

const interceptorConfig = {
  ...partnerConfig?.firebolt,
  formAccess: {
    ...partnerConfig?.firebolt.formAccess,
    formName: 'bb',
  },
}

const withFirebolt = createFireboltProvider(interceptorConfig)

const FormFlow = () => {
  const {
    autofill,
    stepsList,
    onFireboltConnectionError,
    sendProposalModalName,
    closeSendFormModal,
    openedModals,
    customGoBack,
    customChangeStepHandler,
    customOnFormEnd,
    currentStep,
    onBeforeChangeStepHandler,
    goNextStep
  } = useBbOurocardFormFlow()

  const uploadContextValue = useUploadContext()

  const errorRedirect = {
    'application_error': '/produto/bb/erro-no-processamento',
  }
  const { redirectToUrl } = useRedirectTransitionError(errorRedirect)

  const componentClass = classNames(partnerConfig.partnerSlug, `application-${autofill.application?.replace('-dev', '')}`, 'bb-form-account')

  const isPersonalInfo = currentStep?.data?.slug === "personal_info"
  const personalInfoProgress = 15

  const {
    handleAcceptSuggestion,
    handleSkipChangeProductSuggestion,
    suggestionModalOpen,
    downgradeData,
    callSHOnblurField,
    callSHVOnChangeField,
    handleSubmit,
  } = useDowngradeModal({ goNextStep }, currentStep, partnerConfig)

  return (
    <UploadContext.Provider value={uploadContextValue}>
      <StepFormLayout
        application={autofill.application}
        stepsList={stepsList}
        onGoPreviousStep={customGoBack}
        currentStepNumber={currentStep.position}
        partnerLogo={partnerConfig.partnerLogo}
        className={componentClass}
        fixedProgress={isPersonalInfo ? personalInfoProgress : false}
        showOnlyCurrentStep={false} >
        {!redirectToUrl ? (
          <Wizard
            fallback={<LoadingStep />}
            onChangeStep={customChangeStepHandler}
            onConnectionError={onFireboltConnectionError}
            onBeforeChangeStep={onBeforeChangeStepHandler}
            onFinishForm={customOnFormEnd}>
            <Wizard.Step match="*" component={DefaultFormStep} />
            <Wizard.Step
              match={{ slug: 'personal_info' }}
              component={FirstStep}
              handleSubmit={handleSubmit}
              customGoBack={customGoBack}
              partnerFormName={partnerConfig.partnerSlug}
              partnerBrand={partnerConfig?.tracking?.brand}
              onBlurField={callSHOnblurField}
              onChangeField={callSHVOnChangeField}
              autoFill={autofill}
            />
            <Wizard.Step
              match={{ slug: "authorize_sms" }}
              component={AuthorizeSMSStep}
            />
            <Wizard.Step
              match={{ slug: 'complementary_data' }}
              component={ComplementaryData}
            />
            <Wizard.Step
              match={{ slug: 'phone' }}
              component={CustomSelectStep}
              partnerFormName={partnerConfig?.firebolt.formAccess.formName}
              {...phoneNumberStepConfig}
            />
            <Wizard.Step
              match={{ slug: 'token' }}
              component={SMSTokenStep}
              partnerFormName={partnerConfig?.firebolt.formAccess.formName}
            />
            <Wizard.Step
              match={{ slug: 'address_confirmation' }}
              component={CustomSelectStep}
              partnerFormName={partnerConfig?.firebolt.formAccess.formName}
              {...addressStepConfig}
            />
            <Wizard.Step
              match={{ slug: 'account_data' }}
              component={BankDataStep}
              partnerFormName={partnerConfig?.firebolt.formAccess.formName}
            />
            <Wizard.Step //new step
              match={{ slug: 'identity_document' }}
              component={IdentityDocument}
              partnerFormName={partnerConfig?.firebolt.formAccess.formName}
            />
            <Wizard.Step
              match={{ slug: 'identification_data' }}
              component={UploadStep}
              partnerFormName={partnerConfig?.firebolt.formAccess.formName}
            />
            <Wizard.Step
              match={{ slug: 'identity_proof' }}
              partnerFormName={partnerConfig?.firebolt.formAccess.formName}
              component={IdentityProof}
            />

            <Wizard.Step
              match={{ slug: 'residence_data' }}
              partnerFormName={partnerConfig?.firebolt.formAccess.formName}
              component={UploadStep}
              side="frontResidence"
            />

            <Wizard.Step
              match={{ slug: 'occupation' }}
              partnerFormName={partnerConfig?.firebolt.formAccess.formName}
              component={OccupationStep}
            />

            <Wizard.Step
              match={{ slug: 'income_data' }}
              partnerFormName={partnerConfig?.firebolt.formAccess.formName}
              component={IncomeData}
            />

            <Wizard.Step
              match={{ slug: 'proof_of_income' }}
              partnerFormName={partnerConfig?.firebolt.formAccess.formName}
              component={UploadStep}
            />
          </Wizard>
        ) : (
          <LoadingStep />
        )}

      <FormAlerts
        partnerConfig={partnerConfig}
        sendProposalModalName={sendProposalModalName}
        closeSendFormModal={closeSendFormModal}
        openedModals={openedModals}
        handleAcceptSuggestion={handleAcceptSuggestion}
        handleSkipChangeProductSuggestion={handleSkipChangeProductSuggestion}
        suggestionModalOpen={suggestionModalOpen}
        downgradeData={downgradeData}
      />
      </StepFormLayout>
    </UploadContext.Provider>
  )
}

export default withAnalyticsProvider(withFirebolt(FormFlow))
